import { Controller } from "@hotwired/stimulus"
import { createIcons, icons } from "lucide"

/**
 * Outline Scroll Controller
 *
 * A custom scroll navigator for the outline page featuring:
 * - Beat markers showing all 15 beats
 * - Completion status indicators (green checkmarks)
 * - Current position indicator
 * - Click to jump to any beat
 * - Draggable scroll thumb
 * - Smooth scroll animations
 */
export default class extends Controller {
  static targets = ["container", "track", "thumb", "marker"]
  static values = {
    beatCount: { type: Number, default: 15 }
  }

  connect() {
    this.scrollContainer = this.containerTarget
    this.isDragging = false
    this.isHovering = false

    // Build the scroll rail UI
    this.buildScrollRail()

    // Set up scroll listener
    this.scrollContainer.addEventListener("scroll", this.handleScroll.bind(this))

    // Initial position update
    requestAnimationFrame(() => this.updateThumbPosition())

    // Watch for beat completion changes
    this.observeCompletionChanges()
  }

  disconnect() {
    this.scrollContainer?.removeEventListener("scroll", this.handleScroll)
    this.completionObserver?.disconnect()
    this.rail?.remove()
  }

  buildScrollRail() {
    // Create the rail container
    this.rail = document.createElement("div")
    this.rail.className = "outline-scroll-rail"
    this.rail.innerHTML = `
      <div class="scroll-rail-track" data-outline-scroll-target="track">
        <div class="scroll-rail-thumb" data-outline-scroll-target="thumb"></div>
      </div>
      <div class="scroll-rail-markers"></div>
    `

    // Find all beat sections
    const beatSections = this.scrollContainer.querySelectorAll(".beat-section")
    const markersContainer = this.rail.querySelector(".scroll-rail-markers")

    beatSections.forEach((section, index) => {
      const beatId = section.dataset.beatId
      const beatName = section.dataset.beatName || `Beat ${index + 1}`
      const isLocked = section.dataset.beatLocked === "true"
      const isComplete = section.querySelector(".text-lime-500[data-lucide='check-circle-2']") !== null

      const marker = document.createElement("button")
      marker.className = `scroll-rail-marker ${isComplete ? "complete" : ""} ${isLocked ? "locked" : ""}`
      marker.dataset.beatId = beatId
      marker.dataset.index = index
      marker.dataset.outlineScrollTarget = "marker"
      marker.title = beatName
      marker.type = "button"

      // Create the marker content
      marker.innerHTML = `
        <span class="marker-number">${index + 1}</span>
        <span class="marker-status">
          ${isComplete ? '<i data-lucide="check" class="w-3 h-3"></i>' : (isLocked ? '<i data-lucide="lock" class="w-3 h-3"></i>' : '')}
        </span>
        <span class="marker-tooltip">${beatName}</span>
      `

      marker.addEventListener("click", () => this.scrollToBeat(beatId))
      marker.addEventListener("mouseenter", () => this.showTooltip(marker))
      marker.addEventListener("mouseleave", () => this.hideTooltip(marker))

      markersContainer.appendChild(marker)
    })

    // Add to page
    document.body.appendChild(this.rail)

    // Initialize Lucide icons in the rail
    createIcons({ icons, nodes: this.rail.querySelectorAll("[data-lucide]") })

    // Set up thumb dragging
    this.setupDragging()

    // Set up rail hover
    this.setupHover()
  }

  setupDragging() {
    const thumb = this.rail.querySelector(".scroll-rail-thumb")

    thumb.addEventListener("mousedown", (e) => {
      e.preventDefault()
      this.isDragging = true
      this.rail.classList.add("dragging")
      this.dragStartY = e.clientY
      this.dragStartScroll = this.scrollContainer.scrollTop
    })

    document.addEventListener("mousemove", (e) => {
      if (!this.isDragging) return

      const track = this.rail.querySelector(".scroll-rail-track")
      const trackRect = track.getBoundingClientRect()
      const thumbHeight = thumb.offsetHeight

      // Calculate scroll position based on mouse position
      const relativeY = e.clientY - trackRect.top - thumbHeight / 2
      const scrollableHeight = trackRect.height - thumbHeight
      const scrollRatio = Math.max(0, Math.min(1, relativeY / scrollableHeight))

      const maxScroll = this.scrollContainer.scrollHeight - this.scrollContainer.clientHeight
      this.scrollContainer.scrollTop = scrollRatio * maxScroll
    })

    document.addEventListener("mouseup", () => {
      if (this.isDragging) {
        this.isDragging = false
        this.rail.classList.remove("dragging")
      }
    })
  }

  setupHover() {
    this.rail.addEventListener("mouseenter", () => {
      this.isHovering = true
      this.rail.classList.add("expanded")
    })

    this.rail.addEventListener("mouseleave", () => {
      this.isHovering = false
      if (!this.isDragging) {
        this.rail.classList.remove("expanded")
      }
    })
  }

  handleScroll() {
    this.updateThumbPosition()
    this.updateActiveMarker()
  }

  updateThumbPosition() {
    const thumb = this.rail?.querySelector(".scroll-rail-thumb")
    const track = this.rail?.querySelector(".scroll-rail-track")
    if (!thumb || !track) return

    const scrollTop = this.scrollContainer.scrollTop
    const scrollHeight = this.scrollContainer.scrollHeight
    const clientHeight = this.scrollContainer.clientHeight
    const maxScroll = scrollHeight - clientHeight

    if (maxScroll <= 0) {
      thumb.style.display = "none"
      return
    }

    thumb.style.display = "block"

    const trackHeight = track.offsetHeight
    const thumbHeight = thumb.offsetHeight
    const scrollableTrack = trackHeight - thumbHeight

    const scrollRatio = scrollTop / maxScroll
    const thumbTop = scrollRatio * scrollableTrack

    thumb.style.top = `${thumbTop}px`
  }

  updateActiveMarker() {
    const beatSections = this.scrollContainer.querySelectorAll(".beat-section")
    const markers = this.rail?.querySelectorAll(".scroll-rail-marker")
    if (!markers) return

    const containerRect = this.scrollContainer.getBoundingClientRect()
    const containerTop = containerRect.top
    const containerCenter = containerTop + containerRect.height / 3

    let activeIndex = 0

    beatSections.forEach((section, index) => {
      const rect = section.getBoundingClientRect()
      if (rect.top <= containerCenter) {
        activeIndex = index
      }
    })

    markers.forEach((marker, index) => {
      marker.classList.toggle("active", index === activeIndex)
    })
  }

  scrollToBeat(beatId) {
    const beatSection = this.scrollContainer.querySelector(`#beat-${beatId}`)
    if (beatSection) {
      beatSection.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  showTooltip(marker) {
    marker.classList.add("show-tooltip")
  }

  hideTooltip(marker) {
    marker.classList.remove("show-tooltip")
  }

  observeCompletionChanges() {
    // Watch for changes to beat completion status
    this.isUpdatingMarkers = false
    this.completionObserver = new MutationObserver((mutations) => {
      // Prevent infinite loop - don't react to our own changes
      if (this.isUpdatingMarkers) return

      mutations.forEach((mutation) => {
        if (mutation.type === "childList" || mutation.type === "attributes") {
          this.updateMarkerStates()
        }
      })
    })

    const beatSections = this.scrollContainer.querySelectorAll(".beat-section")
    beatSections.forEach((section) => {
      this.completionObserver.observe(section, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"]
      })
    })
  }

  updateMarkerStates() {
    const beatSections = this.scrollContainer.querySelectorAll(".beat-section")
    const markers = this.rail?.querySelectorAll(".scroll-rail-marker")
    if (!markers) return

    // Set flag to prevent infinite loop from MutationObserver
    this.isUpdatingMarkers = true

    beatSections.forEach((section, index) => {
      const isComplete = section.querySelector(".text-lime-500[data-lucide='check-circle-2']") !== null
      const marker = markers[index]
      if (marker) {
        marker.classList.toggle("complete", isComplete)

        // Update the status icon
        const statusSpan = marker.querySelector(".marker-status")
        if (statusSpan && !marker.classList.contains("locked")) {
          if (isComplete) {
            statusSpan.innerHTML = '<i data-lucide="check" class="w-3 h-3"></i>'
          } else {
            statusSpan.innerHTML = ''
          }
          // Re-initialize Lucide
          createIcons({ icons, nodes: statusSpan.querySelectorAll("[data-lucide]") })
        }
      }
    })

    // Reset flag after a brief delay to allow DOM updates to complete
    requestAnimationFrame(() => {
      this.isUpdatingMarkers = false
    })
  }
}
